import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * SignaturePad
 * - HTML5 canvas with mouse + touch support
 * - Exposes onChange(dataUrl) — empty string when cleared
 */
const SignaturePad = ({ value, onChange, height = 240 }) => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(Boolean(value));

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = wrapper.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2.4;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, height);
      img.src = value;
    }
  };

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const data = canvasRef.current.toDataURL("image/png");
    onChange && onChange(data);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = wrapperRef.current.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, height);
    setHasInk(false);
    onChange && onChange("");
  };

  return (
    <div ref={wrapperRef} className="relative bg-white border-2 border-[var(--rs-text)]" data-testid="signature-pad-wrapper">
      <div className="absolute top-3 left-4 rs-overline">{t("sig_baseline")}</div>
      <button
        type="button"
        onClick={clear}
        className="absolute top-2 right-3 text-xs font-bold uppercase tracking-wider text-[var(--rs-text-muted)] hover:text-[var(--rs-primary)] underline underline-offset-4"
        data-testid="signature-clear-btn"
      >
        {t("btn_clear")}
      </button>
      <canvas
        ref={canvasRef}
        className="block w-full cursor-crosshair touch-none"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="absolute left-6 right-6 bottom-10 h-px bg-[var(--rs-border)]" />
      <div className="absolute left-6 bottom-3 text-[10px] tracking-[0.25em] uppercase text-[var(--rs-text-muted)]">
        × ___________________________
      </div>
      {!hasInk && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[var(--rs-text-muted)]/40 text-sm uppercase tracking-[0.3em]">
          {t("sig_baseline")}
        </div>
      )}
    </div>
  );
};

export default SignaturePad;
